// Universal Booking System Types

export type ProviderType = 
  | 'trainer'
  | 'nutritionist'
  | 'physiotherapist'
  | 'yoga'
  | 'crossfit'
  | 'gym'
  | 'pilates'
  | 'dance'
  | 'martial_arts';

export interface UniversalBookingService {
  id: string;
  provider_type: ProviderType;
  provider_id: string;
  name: string;
  description: string | null;
  price: number;
  duration_minutes: number;
  location_type: 'online' | 'in_person' | 'hybrid';
  max_participants: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UniversalBookingLocation {
  id: string;
  provider_type: ProviderType;
  provider_id: string;
  name: string;
  address: string | null;
  city: string | null;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UniversalBookingAvailability {
  id: string;
  provider_type: ProviderType;
  provider_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at: string;
}

export interface UniversalBookingBlockedDate {
  id: string;
  provider_type: ProviderType;
  provider_id: string;
  blocked_date: string;
  reason: string | null;
  created_at: string;
}

export interface UniversalBooking {
  id: string;
  provider_type: ProviderType;
  provider_id: string;
  service_id: string;
  selected_location_id: string | null;
  booking_date: string;
  start_time: string;
  end_time: string;
  client_name: string;
  client_email: string;
  client_phone: string | null;
  client_notes: string | null;
  client_user_id: string | null;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  payment_status: 'pending' | 'paid' | 'refunded' | 'failed';
  payment_method: 'stripe' | 'paypal' | 'in_person' | null;
  total_amount: number;
  payment_intent_id: string | null;
  paypal_order_id: string | null;
  cancellation_reason: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  confirmation_sent: boolean;
  reminder_sent: boolean;
  trainer_notes: string | null;
  created_at: string;
  updated_at: string;
  service?: UniversalBookingService;
}

export interface UniversalBookingSubscription {
  id: string;
  provider_type: ProviderType;
  provider_id: string;
  user_id: string | null;
  subscription_status: 'trial' | 'active' | 'expired' | 'cancelled';
  subscription_tier: 'annual' | 'lifetime' | null;
  trial_started_at: string;
  trial_ends_at: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface BookingAccessStatus {
  hasAccess: boolean;
  status: 'loading' | 'trial' | 'active' | 'expired' | 'none';
  daysRemaining: number | null;
  trialEndsAt: string | null;
  trialStartedAt: string | null;
  subscriptionTier: 'annual' | 'lifetime' | null;
  isLoading: boolean;
}

export interface CreateBookingInput {
  provider_type: ProviderType;
  provider_id: string;
  service_id: string;
  selected_location_id?: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  client_notes?: string;
  payment_method: 'stripe' | 'paypal' | 'in_person';
  total_amount: number;
}

export interface CreateServiceInput {
  provider_type: ProviderType;
  provider_id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes: number;
  location_type?: 'online' | 'in_person' | 'hybrid';
  max_participants?: number;
}

export interface CreateLocationInput {
  provider_type: ProviderType;
  provider_id: string;
  name: string;
  address?: string;
  city?: string;
  is_primary?: boolean;
}

export interface CreateAvailabilityInput {
  provider_type: ProviderType;
  provider_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

// Booking configuration by provider type
export interface BookingConfig {
  enabled: boolean;
  serviceLabels: {
    singular: string;
    plural: string;
  };
  defaultDurations: number[];
  supportsGroupBookings: boolean;
  maxParticipantsDefault: number;
  themeColor: string;
}

export const BOOKING_CONFIGS: Record<ProviderType, BookingConfig> = {
  trainer: {
    enabled: true,
    serviceLabels: { singular: 'Sesión', plural: 'Sesiones' },
    defaultDurations: [30, 45, 60, 90],
    supportsGroupBookings: true,
    maxParticipantsDefault: 5,
    themeColor: 'primary',
  },
  nutritionist: {
    enabled: true,
    serviceLabels: { singular: 'Consulta', plural: 'Consultas' },
    defaultDurations: [30, 45, 60],
    supportsGroupBookings: false,
    maxParticipantsDefault: 1,
    themeColor: 'green',
  },
  physiotherapist: {
    enabled: true,
    serviceLabels: { singular: 'Sesión', plural: 'Sesiones' },
    defaultDurations: [30, 45, 60],
    supportsGroupBookings: false,
    maxParticipantsDefault: 1,
    themeColor: 'blue',
  },
  yoga: {
    enabled: true,
    serviceLabels: { singular: 'Clase', plural: 'Clases' },
    defaultDurations: [60, 75, 90],
    supportsGroupBookings: true,
    maxParticipantsDefault: 15,
    themeColor: 'purple',
  },
  crossfit: {
    enabled: true,
    serviceLabels: { singular: 'Clase', plural: 'Clases' },
    defaultDurations: [60],
    supportsGroupBookings: true,
    maxParticipantsDefault: 20,
    themeColor: 'orange',
  },
  gym: {
    enabled: true,
    serviceLabels: { singular: 'Clase', plural: 'Clases' },
    defaultDurations: [45, 60],
    supportsGroupBookings: true,
    maxParticipantsDefault: 25,
    themeColor: 'green',
  },
  pilates: {
    enabled: true,
    serviceLabels: { singular: 'Clase', plural: 'Clases' },
    defaultDurations: [50, 60],
    supportsGroupBookings: true,
    maxParticipantsDefault: 8,
    themeColor: 'teal',
  },
  dance: {
    enabled: true,
    serviceLabels: { singular: 'Clase', plural: 'Clases' },
    defaultDurations: [60, 90],
    supportsGroupBookings: true,
    maxParticipantsDefault: 20,
    themeColor: 'pink',
  },
  martial_arts: {
    enabled: true,
    serviceLabels: { singular: 'Clase', plural: 'Clases' },
    defaultDurations: [60, 90],
    supportsGroupBookings: true,
    maxParticipantsDefault: 15,
    themeColor: 'red',
  },
};

export const getBookingConfig = (providerType: ProviderType): BookingConfig => {
  return BOOKING_CONFIGS[providerType];
};
